import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getProjectById } from '../../services/projectService';
import GanttChart from '../../components/GanttChart/GanttChart';
import {
  CalendarDays,
  BadgeEuro,
  Clock3,
  FileDown,
  FileScan,
  Building2,
  ListOrdered,
} from 'lucide-react';
//import html2pdf from 'html2pdf.js';
import { saveAs } from 'file-saver';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      const data = await getProjectById(id);
      setProject(data);
    };
    fetchData();
  }, [id]);

  const handleDownloadReport = () => {
    if (project?.reportUrl) {
      saveAs(project.reportUrl, `raport_proiect_${project.id}.pdf`);
    }
  };

  const handleExportPDF = () => {
    if (printRef.current) {
      // html2pdf()
      //   .set({ margin: 0.5, filename: `detalii_proiect_${project.id}.pdf` })
      //   .from(printRef.current)
      //   .save();
    }
  };

  if (!project) {
    return (
      <p className="text-center py-10 text-gray-600 animate-pulse">
        Se încarcă detaliile proiectului...
      </p>
    );
  }

  // Gantt-ul trebuie să primească procedurile mapate corect, cu datele necesare.
  // API-ul returnează extensions (proceduri curente) și futureProcedures (proceduri estimate).
  // Fiecare procedură are: procedureTitle, startDate, endDate, isFuture (doar la extensions).

  // Mapăm toate procedurile într-un format unitar pentru GanttChart
  const ganttProcedures = [
    ...(project.extensions || []).map((proc, idx) => ({
      ...proc,
      type: 'actual',
      index: idx + 1,
      color: '#6366f1', // indigo pentru actuale
      barColor: '#6366f1', // bară indigo
      labelColor: '#312e81', // text indigo închis
    })),
    ...(project.futureProcedures || []).map((proc, idx) => ({
      ...proc,
      type: 'future',
      index: (project.extensions?.length || 0) + idx + 1,
      color: '#a5b4fc', // indigo deschis pentru estimate
      barColor: '#a5b4fc', // bară indigo deschis
      labelColor: '#3730a3', // text indigo mediu
      isFuture: true,
    })),
  ];

  return (
    <section className="max-w-7xl mx-auto px-2 sm:px-6 py-8 space-y-10">
      {/* Summary Bar Sticky */}
      <div className="sticky top-0 z-20 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 border-b border-indigo-100 shadow-sm py-4 px-2 sm:px-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h1
            className="text-2xl sm:text-3xl font-extrabold text-indigo-700 flex items-center gap-2 break-words whitespace-normal max-w-full"
            style={{ wordBreak: 'break-word' }}
          >
            <FileScan size={28} className="text-indigo-400" />
            <span className="block max-w-full break-words whitespace-normal">
              {project.projectTitle}
            </span>
          </h1>
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full">
              <Building2 size={14} /> {project.authorityName}
            </span>
            <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">
              <BadgeEuro size={14} />{' '}
              {project.estimatedValue?.toLocaleString('ro-RO')} RON
            </span>
            <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">
              <Clock3 size={14} /> {project.durationExecution} luni
            </span>
            <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-semibold px-3 py-1 rounded-full">
              <CalendarDays size={14} /> {project.startDate} – {project.endDate}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleDownloadReport}
            className="flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 rounded-lg shadow hover:bg-emerald-700 transition font-semibold text-sm"
          >
            <FileDown size={18} /> Descarcă raport
          </button>
          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg shadow hover:bg-indigo-700 transition font-semibold text-sm"
          >
            <FileScan size={18} /> Export PDF
          </button>
        </div>
      </div>

      {/* Main Content */}
      <section ref={printRef} className="space-y-10">
        {/* Info Card */}
        <section className="bg-white p-8 rounded-2xl shadow-lg border border-indigo-100">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <Building2 size={20} className="text-indigo-600" /> Autoritate
            Contractantă
          </h2>
          <p className="text-base text-gray-700 mb-2 font-medium">
            {project.authorityName}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            <div className="bg-gradient-to-br from-green-50 to-white p-5 rounded-xl border border-green-100 shadow-sm">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                <BadgeEuro size={18} className="text-green-600" /> Valoare
                estimată totală
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {project.estimatedValue?.toLocaleString('ro-RO')} RON
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-white p-5 rounded-xl border border-blue-100 shadow-sm">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                <Clock3 size={18} className="text-blue-600" /> Durată execuție
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {project.durationExecution} luni
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-white p-5 rounded-xl border border-purple-100 shadow-sm">
              <p className="text-xs text-gray-500 mb-1 flex items-center gap-2">
                <CalendarDays size={18} className="text-purple-600" /> Perioadă
                estimată
              </p>
              <p className="text-2xl font-bold text-gray-800">
                {project.startDate} – {project.endDate}
              </p>
            </div>
          </div>
        </section>

        {/* Gantt & Proceduri */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <ListOrdered size={22} className="text-indigo-600" /> Gantt:
              Proceduri
            </h2>
            <div className="w-full overflow-x-auto rounded-2xl border border-indigo-100 bg-white shadow-sm p-2">
              <GanttChart procedures={ganttProcedures} />
            </div>
          </div>

          <aside>
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <ListOrdered size={20} className="text-indigo-600" /> Proceduri
            </h2>
            <div className="space-y-4">
              {project.extensions.map((proc, i) => (
                <div
                  key={i}
                  className="border border-indigo-200 rounded-2xl shadow-sm p-5 bg-white hover:shadow-md transition-all flex flex-col gap-2"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
                        <FileScan size={16} className="text-indigo-400" />{' '}
                        {proc.procedureTitle}
                      </h3>
                      <p className="text-xs text-gray-500 italic">
                        {new Date(proc.startDate).toLocaleDateString('ro-RO')} –{' '}
                        {new Date(proc.endDate).toLocaleDateString('ro-RO')}
                      </p>
                    </div>
                    <span className="inline-block bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full mt-2 sm:mt-0">
                      Etapa {i + 1}
                    </span>
                  </div>
                </div>
              ))}
              {project.futureProcedures?.map((proc, i) => (
                <div
                  key={`f-${i}`}
                  className="border border-dashed border-indigo-300 rounded-2xl shadow-sm p-5 bg-indigo-50 hover:shadow-md transition-all flex flex-col gap-2"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                    <div>
                      <h3 className="text-base font-bold text-indigo-800 mb-1 flex items-center gap-2">
                        <FileScan size={16} className="text-indigo-400" />{' '}
                        {proc.procedureTitle}
                      </h3>
                      <p className="text-xs text-indigo-600 italic">
                        {new Date(proc.startDate).toLocaleDateString('ro-RO')} –{' '}
                        {new Date(proc.endDate).toLocaleDateString('ro-RO')}
                      </p>
                    </div>
                    <span className="inline-block bg-white text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full border border-indigo-400 mt-2 sm:mt-0">
                      Estimată
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </section>
      </section>
    </section>
  );
};

export default ProjectDetail;
